import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { money, financialYear, currentQuarter } from "../lib/format";
import { Button } from "../components/ui/button";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Calculator, User, Building2, Download } from "lucide-react";

const CatRow = ({ label, amount, total }) => (
  <div className="px-4 py-2.5 border-b border-border last:border-0" data-testid={`pnl-cat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className="flex items-center justify-between text-sm">
      <span>{label}</span>
      <span className="num">{money(amount)}</span>
    </div>
    <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
      <div className="h-full bg-primary/60 rounded-full" style={{ width: `${total ? Math.min(100, (amount / total) * 100) : 0}%` }} />
    </div>
  </div>
);

export default function ProfitLossPage() {
  const [period, setPeriod] = useState("year");
  const range = period === "year" ? financialYear() : currentQuarter();
  const [data, setData] = useState(null);
  const [tax, setTax] = useState(null);
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    api.get("/reports/pnl", { params: { date_from: range.from, date_to: range.to } }).then((r) => setData(r.data));
    api.get("/reports/tax", { params: { date_from: range.from, date_to: range.to } }).then((r) => setTax(r.data));
    api.get("/reports/trends", { params: { date_from: range.from, date_to: range.to } }).then((r) => setTrends(r.data.months));
  }, [range.from, range.to]);

  const exportPack = async () => {
    try {
      const res = await api.get("/exports/accountant", { params: { date_from: range.from, date_to: range.to }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accountant-pack-${range.label.replace(/[^a-zA-Z0-9]/g, "-")}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Accountant pack downloaded");
    } catch {
      toast.error("Could not generate the export");
    }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Profit & Loss</h1>
          <p className="text-muted-foreground text-sm mt-1">GST-exclusive figures · {range.label}</p>
        </div>
        <div className="inline-flex rounded-lg border border-border p-1 bg-card">
          <button onClick={() => setPeriod("quarter")} data-testid="pnl-period-quarter"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === "quarter" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Quarter</button>
          <button onClick={() => setPeriod("year")} data-testid="pnl-period-year"
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${period === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Full year</button>
        </div>
        <Button variant="outline" onClick={exportPack} data-testid="accountant-export-btn" className="gap-2">
          <Download className="w-4 h-4" /> Accountant pack
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total income", value: data.total_income, tone: "text-emerald-600" },
          { label: "Total expenses", value: data.total_expense, tone: "text-rose-600" },
          { label: "Net profit", value: data.net_profit, tone: data.net_profit >= 0 ? "text-primary" : "text-rose-600" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-5" data-testid={`pnl-kpi-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{s.label}</div>
            <div className={`num text-2xl font-bold mt-2 ${s.tone}`}>{money(s.value)}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 mb-6 flex items-center justify-between">
        <span className="text-sm font-medium">Operating margin</span>
        <span className="num text-lg font-bold text-primary">{data.operating_margin}%</span>
      </div>

      {data.claimable && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-testid="claimable-summary">
          {[
            { label: "Claimable expenses", value: data.claimable.claimable_expense, tone: "text-emerald-600" },
            { label: "Non-claimable", value: data.claimable.non_claimable_expense, tone: "text-amber-600" },
            { label: "Claimable GST", value: data.claimable.claimable_gst, tone: "text-sky-700" },
            { label: "Personal / drawings", value: data.claimable.personal_total, tone: "text-rose-600" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-border bg-card p-5" data-testid={`claimable-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">{s.label}</div>
              <div className={`num text-xl font-bold mt-2 ${s.tone}`}>{money(s.value)}</div>
            </div>
          ))}
        </div>
      )}

      {trends.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="trends-chart">
          <h3 className="font-heading font-semibold">Profit & GST trend</h3>
          <p className="text-xs text-muted-foreground mb-4">Month-by-month net profit with net GST</p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={trends} margin={{ left: -10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3", fontFamily: "Inter" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="profit" name="Net profit" fill="#113E35" radius={[6, 6, 0, 0]} maxBarSize={44} />
              <Line type="monotone" dataKey="net_gst" name="Net GST" stroke="#0284C7" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {tax && (
        <div className="rounded-2xl border border-border bg-primary text-primary-foreground p-5 mb-6" data-testid="tax-estimate-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-primary-foreground/10 grid place-items-center">
                {tax.entity_type === "company" ? <Building2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider font-mono text-primary-foreground/60 flex items-center gap-1">
                  <Calculator className="w-3 h-3" /> Estimated income tax
                </div>
                <div className="num text-3xl font-bold mt-1" data-testid="tax-estimate-amount">{money(tax.estimated_tax)}</div>
                <div className="text-sm text-primary-foreground/70 mt-1">
                  {tax.rate_used} · {tax.entity_type === "company" ? "Company" : "Sole Trader"} · effective {tax.effective_rate}%
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-primary-foreground/60">on taxable profit</div>
              <div className="num text-lg font-semibold">{money(tax.taxable_profit)}</div>
            </div>
          </div>
          {tax.brackets?.length > 0 && (
            <div className="mt-4 rounded-xl bg-primary-foreground/10 divide-y divide-primary-foreground/10">
              {tax.brackets.map((b, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-primary-foreground/80">{b.range} @ {b.rate}</span>
                  <span className="num">{money(b.tax)}</span>
                </div>
              ))}
              {tax.medicare > 0 && (
                <div className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-primary-foreground/80">Medicare levy 2%</span>
                  <span className="num">{money(tax.medicare)}</span>
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-primary-foreground/50 mt-3">Estimate only — not tax advice. Actual tax depends on offsets, deductions and other income.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm">Income by category</div>
          {data.income_by_category.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">No income</div> :
            data.income_by_category.map((c) => <CatRow key={c.category} label={c.category} amount={c.amount} total={data.total_income} />)}
        </div>
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm">Expenses by category</div>
          {data.expense_by_category.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">No expenses</div> :
            data.expense_by_category.map((c) => <CatRow key={c.category} label={c.category} amount={c.amount} total={data.total_expense} />)}
        </div>
      </div>
    </div>
  );
}
